import { transformRESTFloatToSDKFloat } from '@wix/sdk-runtime/transformations/float';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { resolveUrl } from '@wix/sdk-runtime/rest-modules';
import { ResolveUrlOpts } from '@wix/sdk-runtime/rest-modules';
import { RequestOptionsFactory } from '@wix/sdk-types';

function resolveWixIamRecoveryV1RecoveryServiceUrl(
  opts: Omit<ResolveUrlOpts, 'domainToMappings'>
) {
  const domainToMappings = {
    _: [
      {
        srcPath: '/_iam/recovery',
        destPath: '',
      },
      {
        srcPath: '/_api/iam/recovery',
        destPath: '',
      },
    ],
    'www.wixapis.com': [
      {
        srcPath: '/_api/iam/recovery',
        destPath: '',
      },
    ],
    '*.dev.wix-code.com': [
      {
        srcPath: '/iam/recovery/v1/recover',
        destPath: '/v1/recover',
      },
      {
        srcPath: '/iam/recovery/v1/send-email',
        destPath: '/v1/send-email',
      },
      {
        srcPath: '/iam/recovery/v1/activation-email',
        destPath: '/v1/activation-email',
      },
    ],
  };

  return resolveUrl(Object.assign(opts, { domainToMappings }));
}

const PACKAGE_NAME = '@wix/auto_sdk_identity_recovery';

/**
 * Sends an email to a member with a unique link to a Wix-managed password reset page.
 * The member can use this page to reset their site password.
 * If the site owner uses this page, it resets their Wix account password instead.
 *
 * > **Note:** You must publish the Wix site connected to your Headless project.
 * > Wix uses the published site to display the password reset page. To do this, open your project dashboard,
 * > select **Design Site** from the left panel, and then select **Publish**.
 */
export function sendRecoveryEmail(payload: object): RequestOptionsFactory<any> {
  function __sendRecoveryEmail({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.iam.recovery.v1.recovery',
      method: 'POST' as any,
      methodFqn: 'wix.iam.recovery.v1.RecoveryService.SendRecoveryEmail',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixIamRecoveryV1RecoveryServiceUrl({
        protoPath: '/v1/send-email',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __sendRecoveryEmail;
}

/**
 * Sends an activation email with an activation token
 * making the transition from initial contact to a site member
 */
export function sendActivationEmail(
  payload: object
): RequestOptionsFactory<any> {
  function __sendActivationEmail({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.iam.recovery.v1.recovery',
      method: 'POST' as any,
      methodFqn: 'wix.iam.recovery.v1.RecoveryService.SendActivationEmail',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixIamRecoveryV1RecoveryServiceUrl({
        protoPath: '/v1/activation-email',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __sendActivationEmail;
}

export function recover(payload: object): RequestOptionsFactory<any> {
  function __recover({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.iam.recovery.v1.recovery',
      method: 'POST' as any,
      methodFqn: 'wix.iam.recovery.v1.RecoveryService.Recover',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixIamRecoveryV1RecoveryServiceUrl({
        protoPath: '/v1/recover',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'identity.createdDate' },
              { path: 'identity.updatedDate' },
              { path: 'identity.identityProfile.customFields.value.dateValue' },
              { path: 'additionalData.*.dateValue' },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              { path: 'identity.identityProfile.customFields.value.numValue' },
              { path: 'additionalData.*.numValue' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __recover;
}
